import { DEFAULT_LOCALE, isLocale, type Locale } from '@sjuni/types';
import { useCallback, useMemo, useState } from 'react';

import { EditorLocaleContext } from './EditorLocaleContext';

/*
 * Remembered across sessions. Someone working through the English backlog
 * switches once, not on every screen — and CLAUDE.md rule 5 means the missing
 * translations are what they are looking at all day.
 */
const STORAGE_KEY = 'sjuni.admin.editorLocale.v1';

function readStored(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    return isLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    // Private-mode Safari. Georgian first is the right default anyway.
    return DEFAULT_LOCALE;
  }
}

export function EditorLocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStored);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);

    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not remembering it is a smaller failure than not switching.
    }
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <EditorLocaleContext.Provider value={value}>{children}</EditorLocaleContext.Provider>;
}
